import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Plus, 
  X, 
  Send,
  FileText,
  User,
  CalendarDays
} from 'lucide-react';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp, 
  doc, 
  updateDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const LeaveManagement = ({ 
  batchId, 
  currentUser, 
  isTeacherOrAdmin = false,
  showAdminView = false,
  batch = null
}) => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [leaveData, setLeaveData] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    leaveType: 'sick', // sick, personal, emergency, other
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Load leave requests
  const loadLeaveRequests = useCallback(() => {
    if (!batchId) return;
    
    let q;
    if (showAdminView) {
      // Admin view: show all leave requests for the batch
      q = query(
        collection(db, 'leaveRequests'),
        where('batchId', '==', batchId),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Student view: show only their own requests
      q = query(
        collection(db, 'leaveRequests'),
        where('batchId', '==', batchId),
        where('studentId', '==', currentUser?.uid),
        orderBy('createdAt', 'desc')
      );
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsList = [];
      snapshot.forEach((doc) => {
        requestsList.push({ id: doc.id, ...doc.data() });
      });
      setLeaveRequests(requestsList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading leave requests:', error);
      // If index error, try without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        let fallbackQ;
        if (showAdminView) {
          fallbackQ = query(
            collection(db, 'leaveRequests'),
            where('batchId', '==', batchId)
          );
        } else {
          fallbackQ = query(
            collection(db, 'leaveRequests'),
            where('batchId', '==', batchId),
            where('studentId', '==', currentUser?.uid)
          );
        }
        
        const fallbackUnsubscribe = onSnapshot(fallbackQ, (snapshot) => {
          const requestsList = [];
          snapshot.forEach((doc) => {
            requestsList.push({ id: doc.id, ...doc.data() });
          });
          // Sort client-side by createdAt descending
          requestsList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
          });
          setLeaveRequests(requestsList);
          setLoading(false);
        }, (fallbackError) => {
          console.error('Error loading leave requests (fallback):', fallbackError);
          setLoading(false);
        });
        return fallbackUnsubscribe;
      } else {
        setLoading(false);
      }
    });
    
    return unsubscribe;
  }, [batchId, currentUser?.uid, showAdminView]);

  useEffect(() => {
    if (!batchId || (!currentUser?.uid && !showAdminView)) return;
    
    const unsubscribe = loadLeaveRequests();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [batchId, currentUser?.uid, showAdminView, loadLeaveRequests]);

  const handleSubmitLeaveRequest = async () => {
    if (!leaveData.startDate || !leaveData.endDate || !leaveData.reason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (new Date(leaveData.startDate) > new Date(leaveData.endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    if (!currentUser?.uid) {
      toast.error('User not authenticated');
      return;
    }

    if (!batchId) {
      toast.error('Batch ID not found');
      return;
    }

    setSubmitting(true);
    try {
      const requestData = {
        batchId,
        studentId: currentUser.uid,
        studentName: currentUser.displayName || currentUser.email,
        startDate: leaveData.startDate,
        endDate: leaveData.endDate,
        reason: leaveData.reason,
        leaveType: leaveData.leaveType,
        description: leaveData.description,
        status: 'pending', // pending, approved, rejected
        coachingId: batch?.coachingId || null, // Add coachingId from batch
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'leaveRequests'), requestData);
      
      toast.success('Leave request submitted successfully');
      setShowRequestModal(false);
      setLeaveData({
        startDate: '',
        endDate: '',
        reason: '',
        leaveType: 'sick',
        description: ''
      });
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error(`Failed to submit leave request: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLeaveStatus = async (requestId, newStatus, adminComments = '') => {
    try {
      await updateDoc(doc(db, 'leaveRequests', requestId), {
        status: newStatus,
        adminComments: adminComments,
        reviewedBy: currentUser.uid,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      toast.success(`Leave request ${newStatus} successfully`);
    } catch (error) {
      console.error('Error updating leave status:', error);
      toast.error('Failed to update leave status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const calculateLeaveDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <CalendarDays className="w-6 h-6 mr-2 text-blue-600" />
          {showAdminView ? 'Leave Requests Management' : 'My Leave Requests'}
        </h2>
        {!showAdminView && (
          <button
            onClick={() => setShowRequestModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Request Leave</span>
          </button>
        )}
      </div>

      {/* Statistics for admin view */}
      {showAdminView && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {leaveRequests.filter(r => r.status === 'pending').length}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Approved</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {leaveRequests.filter(r => r.status === 'approved').length}
            </p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Rejected</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {leaveRequests.filter(r => r.status === 'rejected').length}
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {leaveRequests.length}
            </p>
          </div>
        </div>
      )}

      {/* Leave Requests List */}
      {leaveRequests.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{showAdminView ? 'No leave requests found' : 'No leave requests yet'}</p>
          {!showAdminView && (
            <p className="text-sm mt-2">Click "Request Leave" to submit your first request</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {leaveRequests.map((request) => (
            <div key={request.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  {showAdminView && (
                    <div className="flex items-center mb-2">
                      <User className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {request.studentName}
                      </span>
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {request.reason}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="font-medium">Type:</span> {request.leaveType}
                    </div>
                    <div>
                      <span className="font-medium">From:</span> {new Date(request.startDate).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">To:</span> {new Date(request.endDate).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span> {calculateLeaveDays(request.startDate, request.endDate)} day(s)
                    </div>
                    <div>
                      <span className="font-medium">Submitted:</span> {request.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                    </div>
                  </div>
                  {request.description && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Description:</span>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">{request.description}</p>
                    </div>
                  )}
                  {request.adminComments && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Admin Comments:</span>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">{request.adminComments}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)}
                    <span>{request.status.charAt(0).toUpperCase() + request.status.slice(1)}</span>
                  </span>
                  
                  {/* Admin Actions */}
                  {showAdminView && isTeacherOrAdmin && request.status === 'pending' && (
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => handleUpdateLeaveStatus(request.id, 'approved')}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleUpdateLeaveStatus(request.id, 'rejected')}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Request Leave Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Request Leave</h3>
              <button 
                onClick={() => setShowRequestModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Leave Type *
                </label>
                <select
                  value={leaveData.leaveType}
                  onChange={(e) => setLeaveData({ ...leaveData, leaveType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Leave</option>
                  <option value="emergency">Emergency Leave</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={leaveData.startDate}
                  onChange={(e) => setLeaveData({ ...leaveData, startDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={leaveData.endDate}
                  onChange={(e) => setLeaveData({ ...leaveData, endDate: e.target.value })}
                  min={leaveData.startDate || new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason *
                </label>
                <input
                  type="text"
                  value={leaveData.reason}
                  onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })}
                  placeholder="Brief reason for leave"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={leaveData.description}
                  onChange={(e) => setLeaveData({ ...leaveData, description: e.target.value })}
                  placeholder="Additional details about your leave request"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitLeaveRequest}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 flex items-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Submit Request</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;